/**
 * MethodologyFormDialog — crear o editar una metodología del tenant.
 *
 * Mismo modelo de idiomas que las plantillas (OT-0055): se crea en un
 * idioma (código + idioma inicial + nombre y descripción) y al editar se
 * cambia de idioma o se agrega otro con "Editando idioma". Los idiomas
 * salen de `SUPPORTED_LOCALES`: agregar uno no cambia esta pantalla.
 *
 * Sin `methodology` crea (`POST /methodologies`); con `methodology` edita
 * (`PATCH`: nombre, descripción y versión — el código es inmutable). Cada
 * idioma declarado necesita nombre, como exige `MethodologyI18n`.
 *
 * Lo usan la pestaña Metodologías, el detalle, y los diálogos que crean
 * plantillas (crear la metodología ahí mismo).
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import {
  createMethodology,
  METHODOLOGY_CODE_PATTERN,
  updateMethodology,
  type Methodology,
  type MethodologyI18n,
} from '../../../api/methodologies'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useApiErrorMessage } from '../../../hooks/useApiErrorMessage'
import { METHODOLOGIES_QUERY_KEY } from '../../../hooks/useMethodologies'
import {
  editingLocale,
  normalizeLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '../../../i18n/config'
import { pushToast } from '../../../stores/toastStore'
import { Dialog } from '../../Dialog'

const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/

interface Block {
  name: string
  description: string
}
type Blocks = Partial<Record<SupportedLocale, Block>>

interface MethodologyFormDialogProps {
  open: boolean
  onClose: () => void
  /** Presente = edición. */
  methodology?: Methodology | undefined
  onSaved?: ((methodology: Methodology) => void) | undefined
}

export function MethodologyFormDialog(props: MethodologyFormDialogProps) {
  // El formulario se monta al abrir: arranca de los datos vigentes sin
  // sincronizar estado con efectos.
  if (!props.open) return null
  return <MethodologyForm {...props} />
}

function initialBlocks(methodology: Methodology | undefined, uiLocale: SupportedLocale): Blocks {
  if (!methodology) return { [uiLocale]: { name: '', description: '' } }
  const blocks: Blocks = {}
  for (const loc of SUPPORTED_LOCALES) {
    const block = methodology.i18n[loc]
    if (block) blocks[loc] = { name: block.name, description: block.description ?? '' }
  }
  return blocks
}

function toI18n(blocks: Blocks): MethodologyI18n {
  const i18n: MethodologyI18n = {}
  for (const loc of SUPPORTED_LOCALES) {
    const block = blocks[loc]
    if (!block) continue
    const name = block.name.trim()
    const description = block.description.trim()
    i18n[loc] = description ? { name, description } : { name }
  }
  return i18n
}

function MethodologyForm({ open, onClose, methodology, onSaved }: MethodologyFormDialogProps) {
  const { t, i18n } = useTranslation(['templates', 'common'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const apiErrorMessage = useApiErrorMessage()
  const isEdit = methodology !== undefined
  const uiLocale = normalizeLocale(i18n.language)

  const [code, setCode] = useState(methodology?.code ?? '')
  const [version, setVersion] = useState(methodology?.version ?? '1.0.0')
  const [blocks, setBlocks] = useState<Blocks>(() => initialBlocks(methodology, uiLocale))
  const declared = SUPPORTED_LOCALES.filter((loc) => blocks[loc])
  const [activeLocale, setActiveLocale] = useState<SupportedLocale>(() =>
    editingLocale(declared, uiLocale),
  )
  const [submitted, setSubmitted] = useState(false)

  const codeError =
    !isEdit && !METHODOLOGY_CODE_PATTERN.test(code.trim())
      ? t('methodologies.form.code_invalid')
      : null
  const versionError =
    isEdit && !SEMVER_PATTERN.test(version.trim()) ? t('methodologies.form.version_invalid') : null
  const missingName = declared.find((loc) => !blocks[loc]?.name.trim())
  const nameError = missingName
    ? t('methodologies.form.name_required', {
        language: t(`common:languages.${missingName}`),
      })
    : null
  const invalid = codeError !== null || versionError !== null || nameError !== null

  const mutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      const i18nBody = toI18n(blocks)
      return methodology
        ? updateMethodology(accessToken, methodology.id, {
            i18n: i18nBody,
            version: version.trim(),
          })
        : createMethodology(accessToken, { code: code.trim(), i18n: i18nBody })
    },
    onSuccess: (saved) => {
      pushToast({
        kind: 'success',
        message: isEdit
          ? t('methodologies.actions.toast.updated')
          : t('methodologies.actions.toast.created', { name: saved.name }),
      })
      void queryClient.invalidateQueries({ queryKey: METHODOLOGIES_QUERY_KEY })
      onSaved?.(saved)
      onClose()
    },
  })

  const submitting = mutation.isPending
  const close = () => {
    if (!submitting) onClose()
  }
  const submit = () => {
    setSubmitted(true)
    if (!invalid) mutation.mutate()
  }
  const updateBlock = (patch: Partial<Block>) =>
    setBlocks((b) => ({
      ...b,
      [activeLocale]: { ...(b[activeLocale] ?? { name: '', description: '' }), ...patch },
    }))
  // Crear: el idioma inicial reemplaza al único bloque que hay.
  const setInitialLocale = (loc: SupportedLocale) => {
    setBlocks((b) => ({ [loc]: b[activeLocale] ?? { name: '', description: '' } }))
    setActiveLocale(loc)
  }
  // Editar: agregar un idioma arranca con el nombre del vigente, para que
  // la metodología siga siendo válida; el autor lo traduce.
  const addLocale = (loc: SupportedLocale) => {
    setBlocks((b) => ({ ...b, [loc]: { name: b[activeLocale]?.name ?? '', description: '' } }))
    setActiveLocale(loc)
  }

  const block = blocks[activeLocale]
  const inputClass =
    'mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60'

  return (
    <Dialog
      open={open}
      onClose={close}
      title={isEdit ? t('methodologies.edit_dialog.title') : t('methodologies.create_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('methodologies.form.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || (submitted && invalid)}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? t(
                  isEdit
                    ? 'methodologies.edit_dialog.submitting'
                    : 'methodologies.create_dialog.submitting',
                )
              : t(isEdit ? 'methodologies.edit_dialog.submit' : 'methodologies.create_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {isEdit ? (
          <div className="flex items-start gap-6">
            <div>
              <span className="text-xs font-medium text-ink-muted">
                {t('methodologies.form.code')}
              </span>
              <p className="mt-1 font-mono text-sm text-ink">{methodology.code}</p>
            </div>
            <label className="block flex-1">
              <span className="text-xs font-medium text-ink-muted">
                {t('methodologies.form.version')}
              </span>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={submitting}
                spellCheck={false}
                className={`${inputClass} font-mono`}
              />
              <span className="mt-1 block text-[11px] text-ink-subtle">
                {submitted && versionError ? versionError : t('methodologies.form.version_help')}
              </span>
            </label>
          </div>
        ) : (
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">
              {t('methodologies.form.code')}
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder={t('methodologies.form.code_placeholder')}
              disabled={submitting}
              maxLength={12}
              spellCheck={false}
              autoComplete="off"
              aria-invalid={submitted && codeError !== null}
              className={`${inputClass} font-mono`}
            />
            <span
              className={`mt-1 block text-[11px] ${submitted && codeError ? 'text-critical' : 'text-ink-subtle'}`}
            >
              {submitted && codeError ? codeError : t('methodologies.form.code_help')}
            </span>
          </label>
        )}

        {isEdit ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-ink-subtle mr-auto">
              {t('designer.global_config.editing_locale')}
            </span>
            {declared.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => setActiveLocale(loc)}
                aria-pressed={loc === activeLocale}
                className={
                  loc === activeLocale
                    ? 'px-2 py-0.5 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                    : 'px-2 py-0.5 text-xs font-mono uppercase rounded hover:bg-surface-muted text-ink-muted'
                }
              >
                {loc}
              </button>
            ))}
            {SUPPORTED_LOCALES.filter((loc) => !blocks[loc]).map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => addLocale(loc)}
                disabled={submitting}
                title={t('designer.global_config.add_locale')}
                className="px-2 py-0.5 text-xs font-mono uppercase rounded border border-dashed border-line text-ink-subtle hover:text-ink hover:border-line-strong inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                {loc}
              </button>
            ))}
          </div>
        ) : (
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">
              {t('methodologies.form.initial_language')}
            </span>
            <select
              value={activeLocale}
              onChange={(e) => setInitialLocale(e.target.value as SupportedLocale)}
              disabled={submitting}
              className={inputClass}
            >
              {SUPPORTED_LOCALES.map((loc) => (
                <option key={loc} value={loc}>
                  {t(`common:languages.${loc}`)} ({loc})
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[11px] text-ink-subtle">
              {t('methodologies.form.initial_language_help')}
            </span>
          </label>
        )}

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">{t('methodologies.form.name')}</span>
          <input
            type="text"
            value={block?.name ?? ''}
            onChange={(e) => updateBlock({ name: e.target.value })}
            disabled={submitting}
            maxLength={255}
            lang={activeLocale}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('methodologies.form.description')}
          </span>
          <textarea
            value={block?.description ?? ''}
            onChange={(e) => updateBlock({ description: e.target.value })}
            disabled={submitting}
            maxLength={2000}
            rows={3}
            lang={activeLocale}
            className={`${inputClass} resize-y`}
          />
        </label>

        {submitted && nameError ? <p className="text-xs text-critical">{nameError}</p> : null}
        {mutation.error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 text-xs text-critical">
            {apiErrorMessage(mutation.error)}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
